import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Grid, Button, Colors, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import { Field, FieldArray } from 'formik';
import { IoMdTrash } from 'react-icons/io';
import { FaPlus } from 'react-icons/fa';

// types
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// actions
import { deleteTemplateRestrictions } from 'ducks/merchantDucks/restrictions/actions';
import { openModal } from 'ducks/merchantDucks/modal/actions';

// components
import StyledContainer from 'components/StyledContainer';
import Input from 'components/Fields/Input';
import ConfirmModal from 'components/Modals/ConfirmModal';
import Restricted from 'restrictions/roles/components/Restricted';
import { StyledReactIcon } from 'pages/MerchantPages/Audience/components/AudienceDetailsBox';
import FieldError from 'components/Fields/FieldError';

const { Container, Cell } = Grid;

interface Props {
  assetDomainRestrictions: Array<Record<string, string>>;
  isReadOnly?: boolean;
  isTemplate?: boolean;
}

const AssetDomainsForm: FunctionComponent<Props> = ({
  assetDomainRestrictions,
  isReadOnly = false,
  isTemplate = false,
}) => {
  const dispatch = useDispatch();
  const [arrHelpers, setArrayHelpers] = useState({ remove: (i: number) => {} });
  const [itemIndex, setItemIndex] = useState(0);

  const handleDelete = () => {
    arrHelpers.remove(itemIndex);
    if (isTemplate) {
      const { item_id: templateId, id } = assetDomainRestrictions[itemIndex];
      dispatch(deleteTemplateRestrictions(Number(templateId), Number(id)));
    }
  };

  const handleRemoveDomain = (array: any, index: number) => {
    setArrayHelpers(array);
    setItemIndex(index);

    dispatch(openModal(ModalNames.DomainAssetRestriction));
  };

  let ParentStyledContainerProps: { columns: number; margin?: string } = { columns: 1 };
  if (assetDomainRestrictions.length) {
    ParentStyledContainerProps = {
      ...ParentStyledContainerProps,
      margin: '2rem 0 0',
    };
  }

  return (
    <>
      <FieldArray
        name="assetDomainRestrictions"
        render={(arrayHelpers) => {
          return (
            <AnalyticsComponent>
              {({ pages, tracker, merchantId, ip }) => (
                <StyledContainer {...ParentStyledContainerProps}>
                  {assetDomainRestrictions.map((d: Record<string, string>, i: number) => {
                    return (
                      <Container columns={4} key={i}>
                        <Cell width={3}>
                          <Field
                            tag={`text_input_add_another_url_${i}`}
                            name={`assetDomainRestrictions[${i}].domain`}
                            maxLength={255}
                            type="text"
                            component={Input}
                            placeholder="Enter URL (e.g. https://yoursite.com/livestreams)"
                            disabled={isReadOnly}
                          />
                          <FieldError name={`assetDomainRestrictions[${i}].domain`} />
                        </Cell>
                        <Restricted isRestricted={isReadOnly}>
                          <Cell width={1}>
                            <StyledReactIcon
                              as={IoMdTrash}
                              color={Colors.red}
                              onClick={() => {
                                tracker.track({
                                  event: 'click',
                                  type: 'icon',
                                  tag: `button_delete_${i}`,
                                  pages,
                                  merchantId,
                                  ip,
                                });
                                handleRemoveDomain(arrayHelpers, i);
                              }}
                            />
                          </Cell>
                        </Restricted>
                      </Container>
                    );
                  })}
                  <Restricted isRestricted={isReadOnly}>
                    <StyledContainer columns="minmax(50%, 35rem)" margin="1rem 0 2rem 0">
                      <Cell left={1}>
                        <Button
                          tag="button_add_another_url"
                          type="button"
                          onClick={() => arrayHelpers.push({ id: 0, domain: '' })}
                          buttonModifiers={['buttonLink']}
                        >
                          <FaPlus />
                          {!assetDomainRestrictions.length ? 'Add a URL' : 'Add another URL'}
                        </Button>
                      </Cell>
                    </StyledContainer>
                  </Restricted>
                </StyledContainer>
              )}
            </AnalyticsComponent>
          );
        }}
      />
      <ConfirmModal
        modalName={ModalNames.DomainAssetRestriction}
        handleConfirm={handleDelete}
        description="Your asset will no longer be allowed on this URL. If there are no other URLs added, the asset will have no URL restrictions."
        message="Are you sure you want to delete the URL?"
        title="Delete URL"
        subheader="You can always reactivate the URL later!"
      />
    </>
  );
};

export default AssetDomainsForm;
